import React from 'react';
import Modal from '../ui/Modal';
import { getModifierKeySymbol } from '../../utils/platform';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutItem {
  keys: string;
  description: string;
}

interface ShortcutSection {
  title: string;
  emoji: string;
  shortcuts: ShortcutItem[];
}

// Component to render keyboard shortcut with individual keys
const KeyboardShortcut: React.FC<{ keys: string }> = ({ keys }) => {
  // Split by + and handle special cases like "← / →"
  const keyParts = keys.includes(' / ')
    ? keys.split(' / ').map((part) => part.trim())
    : keys.split('+').map((part) => part.trim());

  return (
    <div className="flex items-center gap-1">
      {keyParts.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {keys.includes(' / ') ? '/' : '+'}
            </span>
          )}
          <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm">
            {key}
          </kbd>
        </React.Fragment>
      ))}
    </div>
  );
};

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const modKey = getModifierKeySymbol();

  const shortcutSections: ShortcutSection[] = [
    {
      title: 'Undo/Redo',
      emoji: '🔄',
      shortcuts: [
        { keys: `${modKey}+Z`, description: 'Undo last edit' },
        { keys: `${modKey}+Shift+Z`, description: 'Redo' },
        { keys: `${modKey}+Y`, description: 'Redo (alternative)' },
      ],
    },
    {
      title: 'Transcript Editing',
      emoji: '✏️',
      shortcuts: [
        { keys: 'Enter', description: 'Split utterance at cursor' },
        {
          keys: 'Backspace',
          description: 'Merge with previous (at start)',
        },
        { keys: 'Tab', description: 'Play/pause media' },
        { keys: `${modKey}+I`, description: 'Cycle word confidence state' },
      ],
    },
    {
      title: 'Search & Navigation',
      emoji: '🔍',
      shortcuts: [
        { keys: 'Enter', description: 'Next search result' },
        { keys: 'Shift+Enter', description: 'Previous search result' },
      ],
    },
    {
      title: 'Save Edits',
      emoji: '💾',
      shortcuts: [
        {
          keys: `${modKey}+Enter`,
          description: 'Save changes (Summary/Notes)',
        },
        { keys: 'Enter', description: 'Save changes (Speaker info)' },
        { keys: 'Escape', description: 'Cancel editing' },
      ],
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Keyboard Shortcuts & Quick Actions"
      maxWidth="4xl"
    >
      <div className="space-y-6">
        {/* Grid layout for wide screens, single column for narrow */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {shortcutSections.map((section, index) => (
            <div key={index}>
              <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <span className="text-base">{section.emoji}</span>
                <span>{section.title}</span>
              </h3>
              <div className="space-y-1">
                {section.shortcuts.map((shortcut, shortcutIndex) => (
                  <div
                    key={shortcutIndex}
                    className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded"
                  >
                    <KeyboardShortcut keys={shortcut.keys} />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Quick Tips Section */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <span className="text-base">⚡</span>
            <span>Quick Tips</span>
          </h4>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span className="flex items-center gap-1 flex-wrap">
                Standard shortcuts work:{' '}
                <span className="inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    {modKey}
                  </kbd>
                  <span className="text-xs">+</span>
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    C
                  </kbd>
                </span>{' '}
                (copy),{' '}
                <span className="inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    {modKey}
                  </kbd>
                  <span className="text-xs">+</span>
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    V
                  </kbd>
                </span>{' '}
                (paste),{' '}
                <span className="inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    {modKey}
                  </kbd>
                  <span className="text-xs">+</span>
                  <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">
                    A
                  </kbd>
                </span>{' '}
                (select all)
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Paste automatically removes formatting</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                Click any word in transcript to jump to that moment in audio
              </span>
            </li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default KeyboardShortcutsModal;
